import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Navbar from "../../../components/Navbar";
import API from "../../../api/api";
import { toastMessageAlert } from "../../../utils/alert";

const parseScorePair = (value) => {
  const match = String(value || "").match(/(\d+)\s*[-:]\s*(\d+)/);
  if (!match) return [0, 0];
  return [Number(match[1]) || 0, Number(match[2]) || 0];
};
const hasSideId = (value) => Number(value || 0) > 0;
const isTeamBracketMatch = (match) =>
  hasSideId(match?.team1_id) || hasSideId(match?.team2_id);
const normalizeGenderToken = (value) => {
  const raw = String(value || "").toLowerCase().trim();
  if (raw === "male" || raw === "men" || raw === "m") return "male";
  if (raw === "female" || raw === "women" || raw === "f") return "female";
  return "all";
};
const normalizeGameType = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/\s*\/\s*/g, "_")
    .replace(/\s+/g, "_");
const isBracketTeamStyleGame = (game = {}) => {
  const gameType = normalizeGameType(game?.game_type);
  if (gameType === "TEAM_SPORT") return true;

  const text = `${String(game?.game_name || "").toLowerCase()} ${String(game?.game_narration || "").toLowerCase()}`;
  const hasSingles = /\bsingle(s)?\b/.test(text);
  const hasDoubles = /\bdouble(s)?\b/.test(text) || /\bpair(s)?\b/.test(text) || /\bduo\b/.test(text);
  const hasTeamWord = /\bteam\b/.test(text) || /\brelay\b/.test(text);
  const hasMixed = /\bmixed\b/.test(text);

  if (hasSingles && !hasDoubles && !hasTeamWord) return false;
  if (hasDoubles || hasTeamWord) return true;
  if (hasMixed && !hasSingles) return true;
  return false;
};
const toUniqueClassificationIds = (value) =>
  Array.from(
    new Set(
      (Array.isArray(value) ? value : [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0)
        .map((id) => String(id))
    )
  );
const generatedSheetClassificationText = (row) => {
  const names = (Array.isArray(row?.classification_names) ? row.classification_names : [])
    .map((name) => String(name || "").trim())
    .filter(Boolean);
  const ids = toUniqueClassificationIds(row?.classification_ids);
  const includeUnclassified = Boolean(row?.include_unclassified);
  const base = names.length
    ? names.join(" + ")
    : (ids.length ? ids.map((id) => `Class ${id}`).join(" + ") : "All classifications");
  return includeUnclassified ? `${base} + Missed / New Player` : base;
};
const pickGeneratedSheetForResults = (rows, games, activeGender, preferredGameId = "") => {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return null;
  const availableGameIds = new Set(
    (Array.isArray(games) ? games : [])
      .map((g) => String(g?.id || "").trim())
      .filter(Boolean)
  );
  const filteredByGame = list.filter((row) => {
    const gameId = Number(row?.game_id || 0);
    if (gameId <= 0) return true;
    if (!availableGameIds.size) return false;
    return availableGameIds.has(String(gameId));
  });
  if (!filteredByGame.length) return null;

  const preferred = String(preferredGameId || "").trim();
  let scopedRows = filteredByGame;
  if (preferred) {
    const preferredRows = filteredByGame.filter((row) => String(row?.game_id || "") === preferred);
    const globalRows = filteredByGame.filter((row) => Number(row?.game_id || 0) <= 0);
    if (preferredRows.length) {
      scopedRows = [...preferredRows, ...globalRows];
    } else if (globalRows.length) {
      scopedRows = globalRows;
    }
  }

  const wantedGender = normalizeGenderToken(activeGender);
  if (wantedGender === "all") {
    return scopedRows.find((row) => normalizeGenderToken(row?.gender) === "all") || scopedRows[0];
  }
  return (
    scopedRows.find((row) => normalizeGenderToken(row?.gender) === wantedGender) ||
    scopedRows.find((row) => normalizeGenderToken(row?.gender) === "all") ||
    scopedRows[0]
  );
};
const formatGeneratedUpdatedAt = (value) => {
  const date = new Date(String(value || ""));
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};
const buttonPadBase =
  "rounded-2xl px-4 py-3 text-sm sm:text-base font-bold text-white select-none transition-all active:translate-y-[2px] focus:outline-none focus:ring-2 focus:ring-offset-2";
const buttonPadBlue =
  "bg-gradient-to-b from-sky-500 to-blue-700 shadow-[0_5px_0_#1d4ed8,0_12px_24px_rgba(30,64,175,0.35)] hover:brightness-110 focus:ring-blue-400";
const buttonPadEmerald =
  "bg-gradient-to-b from-emerald-400 to-emerald-700 shadow-[0_5px_0_#0f766e,0_12px_24px_rgba(15,118,110,0.35)] hover:brightness-110 focus:ring-emerald-400";
const buttonPadRose =
  "bg-gradient-to-b from-rose-400 to-rose-700 shadow-[0_5px_0_#be123c,0_12px_24px_rgba(190,24,93,0.35)] hover:brightness-110 focus:ring-rose-400";

const BracketManager = () => {
  const navigate = useNavigate();
  const { event_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialGender = useMemo(() => {
    const g = (searchParams.get("gender") || "all").toLowerCase();
    return ["all", "male", "female"].includes(g) ? g : "all";
  }, [searchParams]);
  const [gender, setGender] = useState(initialGender);
  const date = searchParams.get("date") || "";
  const source = String(searchParams.get("source") || "").toLowerCase();
  const gameIdFromQuery = searchParams.get("game_id") || "";
  const [selectedGameId, setSelectedGameId] = useState(gameIdFromQuery || "");
  const gameDayContextQuery = useMemo(() => {
    const q = new URLSearchParams();
    if (date) q.set("date", date);
    if (event_id) q.set("event_id", String(event_id));
    q.set("gender", gender || "all");
    return q.toString();
  }, [date, event_id, gender]);
  const gameEngineContextQuery = useMemo(() => {
    const q = new URLSearchParams(gameDayContextQuery);
    const activeGameId = selectedGameId || gameIdFromQuery;
    q.set("source", "game-engine");
    if (activeGameId && String(activeGameId) !== "all") {
      q.set("game_id", String(activeGameId));
    }
    return q.toString();
  }, [gameDayContextQuery, selectedGameId, gameIdFromQuery]);
  const backTarget =
    source === "game-engine"
      ? `/admin/game-engine?${gameEngineContextQuery}`
      : `/admin/game-day?${gameDayContextQuery}`;
  const backLabel = source === "game-engine" ? "Back to Game Engine" : "Back to Game Day";
  const activeGenderLabel = gender === "male" ? "Male" : gender === "female" ? "Female" : "All";
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [games, setGames] = useState([]);
  const [matches, setMatches] = useState([]);
  const [competitionType, setCompetitionType] = useState("BRACKET_INDIVIDUAL");
  const [selectedClassificationId, setSelectedClassificationId] = useState("");
  const [generatedMixedClassificationIds, setGeneratedMixedClassificationIds] = useState([]);
  const [generatedMixedLabel, setGeneratedMixedLabel] = useState("");
  const [appliedGeneratedSheet, setAppliedGeneratedSheet] = useState(null);
  const [roundName, setRoundName] = useState("Round 1");
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [message, setMessage] = useState("");
  const [gameMode, setGameMode] = useState("manual");
  const [scoreByMatch, setScoreByMatch] = useState({});
  const [winnerByMatch, setWinnerByMatch] = useState({});
  const [liveMatchId, setLiveMatchId] = useState("");
  const [liveScore, setLiveScore] = useState({ left: 0, right: 0 });
  const [liveStep, setLiveStep] = useState(1);
  const [teamsLoaded, setTeamsLoaded] = useState(false);

  useEffect(() => {
    if (initialGender !== gender) {
      setGender(initialGender);
    }
  }, [initialGender, gender]);

  useEffect(() => {
    if (!message) return;
    toastMessageAlert(message, { timer: 2400 });
  }, [message]);

  useEffect(() => {
    let alive = true;
    if (!event_id) return undefined;

    const loadContext = async () => {
      try {
        const [contextResult, generatedResult] = await Promise.allSettled([
          API.fetchResultEventContext(event_id),
          API.fetchGeneratedScoreSheets({ event_id: Number(event_id) }),
        ]);
        if (!alive) return;

        const ctx =
          contextResult.status === "fulfilled"
            ? (contextResult.value?.data || contextResult.value || {})
            : {};
        const list = Array.isArray(ctx?.games) ? ctx.games : [];
        setGames(list);
        const queryGameId =
          gameIdFromQuery && list.some((game) => String(game.id) === String(gameIdFromQuery))
            ? String(gameIdFromQuery)
            : "";

        const rows = generatedResult.status === "fulfilled" && Array.isArray(generatedResult.value)
          ? generatedResult.value
          : [];
        const picked = pickGeneratedSheetForResults(rows, list, gender, queryGameId);
        if (!picked) {
          setSelectedGameId(queryGameId);
          setSelectedClassificationId("");
          setGeneratedMixedClassificationIds([]);
          setGeneratedMixedLabel("");
          setAppliedGeneratedSheet(null);
          return;
        }

        const generatedClassIds = toUniqueClassificationIds(picked?.classification_ids);
        const scopedGameIds = new Set(list.map((game) => String(game?.id || "").trim()).filter(Boolean));
        const preferredGameId =
          picked?.game_id && scopedGameIds.has(String(picked.game_id)) ? String(picked.game_id) : "";
        const resolvedGameId = preferredGameId || queryGameId;
        const selectedGameName =
          list.find((game) => String(game.id) === String(resolvedGameId))?.game_name ||
          (resolvedGameId ? `Game ${resolvedGameId}` : "All Games");
        const combinedLabel = generatedSheetClassificationText(picked);

        setSelectedGameId(resolvedGameId);
        if (generatedClassIds.length >= 2) {
          setSelectedClassificationId("__mixed__");
          setGeneratedMixedClassificationIds(generatedClassIds);
          setGeneratedMixedLabel(combinedLabel);
        } else if (generatedClassIds.length === 1) {
          setSelectedClassificationId(generatedClassIds[0]);
          setGeneratedMixedClassificationIds([]);
          setGeneratedMixedLabel("");
        } else {
          setSelectedClassificationId("");
          setGeneratedMixedClassificationIds([]);
          setGeneratedMixedLabel("");
        }
        setAppliedGeneratedSheet({
          id: picked?.id ?? null,
          mode: generatedClassIds.length >= 2 ? "Combined Categories" : "Individual Categories",
          gameName: selectedGameName,
          categoryText: combinedLabel,
          totalPlayers: Number.isFinite(Number(picked?.total_players)) ? Number(picked.total_players) : null,
          updatedAt: formatGeneratedUpdatedAt(picked?.updated_at || picked?.created_at),
        });
      } catch (_error) {
        if (!alive) return;
        setCompetitionType("BRACKET_INDIVIDUAL");
        setGames([]);
      }
    };

    loadContext();
    return () => {
      alive = false;
    };
  }, [event_id, gender, gameIdFromQuery]);

  useEffect(() => {
    const selected = games.find((g) => String(g.id) === String(selectedGameId));
    const type = normalizeGameType(selected?.game_type || "");
    if (type) {
      setCompetitionType(type);
    }
  }, [games, selectedGameId]);

  useEffect(() => {
    if (!event_id) return;
    setTeamsLoaded(false);
    Promise.all([
      API.fetchEventPlayers(event_id, {
        gender: gender === "all" ? undefined : gender,
        game_id: selectedGameId || undefined,
      }),
      API.listTeams(event_id, selectedGameId ? { game_id: selectedGameId } : {}),
      API.listMatches(event_id, selectedGameId ? { game_id: selectedGameId } : {}),
    ])
      .then(([playersRes, teamsRes, matchesRes]) => {
        const playerRows = playersRes?.data || playersRes || [];
        const allPlayers = Array.isArray(playerRows) ? playerRows : [];
        setPlayers(allPlayers);
        setTeams(teamsRes?.data || teamsRes || []);
        setMatches(matchesRes?.data || matchesRes || []);
      })
      .catch(() => {
        setPlayers([]);
        setTeams([]);
        setMatches([]);
      })
      .finally(() => setTeamsLoaded(true));
  }, [event_id, selectedGameId, gender]);

  const activeClassificationIds = useMemo(() => {
    if (selectedClassificationId === "__mixed__") {
      return generatedMixedClassificationIds.map((id) => String(id));
    }
    if (selectedClassificationId) {
      return [String(selectedClassificationId)];
    }
    return [];
  }, [selectedClassificationId, generatedMixedClassificationIds]);

  const filteredPlayers = players.filter((p) => {
    if (selectedGameId && String(p.game_id) !== String(selectedGameId)) return false;
    if (!activeClassificationIds.length) return true;
    return activeClassificationIds.includes(String(p.classification_id || ""));
  });
  const playerClassById = useMemo(
    () =>
      players.reduce((acc, row) => {
        acc[String(row.player_id)] = String(row.classification_id || "");
        return acc;
      }, {}),
    [players]
  );
  const filteredTeams = teams.filter((t) => {
    if (selectedGameId && String(t.game_id) !== String(selectedGameId)) return false;
    if (!activeClassificationIds.length) return true;
    const teamClassId = String(t.classification_id || "");
    if (teamClassId) return activeClassificationIds.includes(teamClassId);

    const members = Array.isArray(t.members) ? t.members : [];
    if (!members.length) return true;
    return members.every((member) =>
      activeClassificationIds.includes(playerClassById[String(member?.player_id || "")] || "")
    );
  });

  const classificationOptions = [
    ...new Map(
      players
        .filter((p) => p.classification_id)
        .map((p) => [String(p.classification_id), p.classification_name || `Class ${p.classification_id}`])
    ).entries(),
  ];

  const selectedGame = games.find((g) => String(g.id) === String(selectedGameId));
  const isTeamBracketGame = isBracketTeamStyleGame(selectedGame);
  const isTeamMode = competitionType === "TEAM_SPORT" || isTeamBracketGame;
  const teamManagerQuery = useMemo(() => {
    const q = new URLSearchParams(gameDayContextQuery);
    if (source) q.set("source", source);
    if (selectedGameId && String(selectedGameId) !== "all") q.set("game_id", String(selectedGameId));
    return q.toString();
  }, [gameDayContextQuery, source, selectedGameId]);
  useEffect(() => {
    if (!event_id) return;
    if (!selectedGameId) return;
    if (!isTeamMode) return;
    if (!teamsLoaded) return;
    if (filteredTeams.length > 0) return;
    navigate(`/admin/results/team/${event_id}?${teamManagerQuery}`, { replace: true });
  }, [event_id, selectedGameId, isTeamMode, teamsLoaded, filteredTeams.length, teamManagerQuery, navigate]);

  const p1Options = (isTeamMode ? filteredTeams : filteredPlayers).filter((x) =>
    p2 ? String(isTeamMode ? x.id : x.registration_id) !== String(p2) : true
  );
  const p2Options = (isTeamMode ? filteredTeams : filteredPlayers).filter((x) =>
    p1 ? String(isTeamMode ? x.id : x.registration_id) !== String(p1) : true
  );

  const createMatch = async () => {
    if (!selectedGameId) {
      setMessage("Select a game before creating a match.");
      return;
    }
    if (!p1 || !p2) {
      setMessage(isTeamMode ? "Select both teams." : "Select both players.");
      return;
    }
    if (String(p1) === String(p2)) {
      setMessage(isTeamMode ? "Team 1 and Team 2 must be different." : "Player 1 and Player 2 must be different.");
      return;
    }
    try {
      const payload = {
        game_id: Number(selectedGameId),
        round_name: roundName,
        player1_reg_id: isTeamMode ? null : Number(p1),
        player2_reg_id: isTeamMode ? null : Number(p2),
        team1_id: isTeamMode ? Number(p1) : null,
        team2_id: isTeamMode ? Number(p2) : null,
        match_order: matches.length + 1,
      };
      await API.createMatch(event_id, payload);
      const refreshed = await API.listMatches(event_id, { game_id: selectedGameId });
      setMatches(refreshed?.data || refreshed || []);
      setMessage("Match created.");
    } catch (err) {
      setMessage(err?.message || "Error creating match.");
    }
  };

  const saveResult = async (match, winnerId, score) => {
    const matchId = Number(match?.id || 0);
    if (!matchId) return null;
    const matchIsTeam = isTeamBracketMatch(match);
    try {
      await API.saveMatchResult(matchId, {
        winner_reg_id: matchIsTeam ? null : Number(winnerId || 0),
        winner_team_id: matchIsTeam ? Number(winnerId || 0) : null,
        score,
      });
      const refreshed = await API.listMatches(event_id, { game_id: selectedGameId });
      setMatches(refreshed?.data || refreshed || []);
      setMessage("Match result saved.");
      return refreshed?.data || refreshed || [];
    } catch (err) {
      setMessage(err?.message || "Save failed.");
      return null;
    }
  };

  useEffect(() => {
    if (!matches.length) {
      setLiveMatchId("");
      return;
    }
    const hasLive = matches.some((m) => String(m.id) === String(liveMatchId));
    if (!hasLive) setLiveMatchId(String(matches[0].id));
  }, [matches, liveMatchId]);

  const selectedLiveMatch = matches.find((m) => String(m.id) === String(liveMatchId)) || null;
  const selectedLiveMatchIsTeam = isTeamBracketMatch(selectedLiveMatch);

  useEffect(() => {
    if (!selectedLiveMatch) {
      setLiveScore({ left: 0, right: 0 });
      return;
    }
    const mapped = scoreByMatch[selectedLiveMatch.id];
    const scoreText = mapped !== undefined ? mapped : selectedLiveMatch.score || "";
    const [left, right] = parseScorePair(scoreText);
    setLiveScore({ left, right });
  }, [selectedLiveMatch, scoreByMatch]);

  const liveLeftId = selectedLiveMatch
    ? String(
      selectedLiveMatchIsTeam
        ? selectedLiveMatch.team1_id || ""
        : selectedLiveMatch.player1_reg_id || ""
    )
    : "";
  const liveRightId = selectedLiveMatch
    ? String(
      selectedLiveMatchIsTeam
        ? selectedLiveMatch.team2_id || ""
        : selectedLiveMatch.player2_reg_id || ""
    )
    : "";
  const liveLeftLabel = selectedLiveMatch
    ? (selectedLiveMatchIsTeam
      ? selectedLiveMatch.team1_name
      : selectedLiveMatch.player1_name) ||
      (liveLeftId ? "Left" : "BYE")
    : "Left";
  const liveRightLabel = selectedLiveMatch
    ? (selectedLiveMatchIsTeam
      ? selectedLiveMatch.team2_name
      : selectedLiveMatch.player2_name) ||
      (liveRightId ? "Right" : "BYE")
    : "Right";
  const liveWinnerId = selectedLiveMatch
    ? String(winnerByMatch[selectedLiveMatch.id] ?? (selectedLiveMatch.winner_id || ""))
    : "";

  const bumpLive = (side, delta) => {
    setLiveScore((prev) => ({
      ...prev,
      [side]: Math.max(0, Number(prev[side] || 0) + delta),
    }));
  };
  const setWinnerForSide = (side) => {
    if (!selectedLiveMatch) return;
    const winnerId = side === "left" ? liveLeftId : liveRightId;
    setWinnerByMatch((prev) => ({ ...prev, [selectedLiveMatch.id]: winnerId }));
  };
  const resetLiveScore = () => {
    setLiveScore({ left: 0, right: 0 });
    if (selectedLiveMatch) {
      setWinnerByMatch((prev) => ({ ...prev, [selectedLiveMatch.id]: "" }));
      setScoreByMatch((prev) => ({ ...prev, [selectedLiveMatch.id]: "0-0" }));
    }
  };

  const syncLiveToForm = () => {
    if (!selectedLiveMatch) {
      setMessage("Select a match for live control.");
      return;
    }
    const scoreText = `${liveScore.left}-${liveScore.right}`;
    setScoreByMatch((prev) => ({ ...prev, [selectedLiveMatch.id]: scoreText }));
    let winner = "";
    if (liveLeftId && !liveRightId) winner = liveLeftId;
    if (liveRightId && !liveLeftId) winner = liveRightId;
    if (!winner && liveScore.left > liveScore.right) winner = liveLeftId;
    if (!winner && liveScore.right > liveScore.left) winner = liveRightId;
    setWinnerByMatch((prev) => ({ ...prev, [selectedLiveMatch.id]: winner }));
    setMessage("Live score synced to match form.");
  };

  const saveLive = async (moveToNext = false) => {
    if (!selectedLiveMatch) {
      setMessage("Select a match for live save.");
      return;
    }
    const scoreText = `${liveScore.left}-${liveScore.right}`;
    const explicitWinner = winnerByMatch[selectedLiveMatch.id] || "";
    let winner = explicitWinner;
    if (!winner) {
      if (liveLeftId && !liveRightId) winner = liveLeftId;
      if (liveRightId && !liveLeftId) winner = liveRightId;
      if (!winner && liveScore.left > liveScore.right) winner = liveLeftId;
      if (!winner && liveScore.right > liveScore.left) winner = liveRightId;
    }
    if (!winner) {
      setMessage("Select a winner or create a non-tied score.");
      return;
    }
    const currentMatchId = String(selectedLiveMatch.id);
    const refreshed = await saveResult(selectedLiveMatch, winner, scoreText);
    if (!moveToNext) return;

    const updated = Array.isArray(refreshed) && refreshed.length ? refreshed : matches;
    const idx = updated.findIndex((m) => String(m.id) === currentMatchId);
    const next = idx >= 0 ? updated[idx + 1] : null;
    if (next?.id) {
      setLiveMatchId(String(next.id));
    }
  };

  const handleGenderChange = (nextGender) => {
    const normalized = ["all", "male", "female"].includes(nextGender) ? nextGender : "all";
    setGender(normalized);
    const q = new URLSearchParams(searchParams);
    q.set("gender", normalized);
    setSearchParams(q, { replace: true });
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto p-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-900">Bracket Manager</h1>
            <div className="flex gap-2 items-center">
              <label className="text-sm text-gray-700">
                Gender
                <select
                  value={gender}
                  onChange={(e) => handleGenderChange(e.target.value)}
                  className="ml-2 rounded border border-gray-300 px-2 py-1"
                >
                  <option value="all">All</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </label>
              <button
                onClick={() => navigate(backTarget)}
                className="text-sm px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300"
              >
                {backLabel}
              </button>
            </div>
          </div>
          <p className="text-xs text-blue-700 mb-3">Active Filter: Gender = {activeGenderLabel}</p>

          {message ? <div className="mb-3 text-sm bg-gray-100 p-3 rounded-lg">{message}</div> : null}

          <div className="mb-4 rounded-xl border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Game Mode</span>
            <button
              type="button"
              onClick={() => setGameMode("manual")}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                gameMode === "manual" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Manual
            </button>
            <button
              type="button"
              onClick={() => setGameMode("live")}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                gameMode === "live" ? "bg-emerald-700 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Live
            </button>
          </div>

          {gameMode === "live" ? (
            <div className="bg-white rounded-xl shadow p-5 mb-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h2 className="font-semibold text-slate-900">Live Match Pad</h2>
                <div className="text-xs text-slate-600">
                  Tap large controls, lock winner, then <span className="font-semibold">Save & Next</span>.
                </div>
              </div>
              <div className="grid lg:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <select
                    value={liveMatchId}
                    onChange={(e) => setLiveMatchId(e.target.value)}
                    className="border p-2 rounded w-full"
                  >
                    <option value="">Select match</option>
                    {matches.map((m) => (
                      <option key={m.id} value={m.id}>
                        {isTeamBracketMatch(m)
                          ? `${m.team1_name || (hasSideId(m.team1_id) ? "Team 1" : "BYE")} vs ${m.team2_name || (hasSideId(m.team2_id) ? "Team 2" : "BYE")}`
                          : `${m.player1_name || (hasSideId(m.player1_reg_id) ? "Player 1" : "BYE")} vs ${m.player2_name || (hasSideId(m.player2_reg_id) ? "Player 2" : "BYE")}`}
                      </option>
                    ))}
                  </select>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {[1, 2, 3, 5].map((step) => (
                      <button
                        key={step}
                        type="button"
                        onClick={() => setLiveStep(step)}
                        className={`rounded-lg border px-3 py-2 font-semibold ${
                          liveStep === step
                            ? "border-blue-700 bg-blue-700 text-white"
                            : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        Step {step}
                      </button>
                    ))}
                  </div>
                  <div className="rounded-2xl bg-slate-900 text-emerald-300 p-5 font-mono text-6xl text-center shadow-inner tracking-wider">
                    {liveScore.left}-{liveScore.right}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setWinnerForSide("left")}
                      disabled={!liveLeftId}
                      className={`rounded-xl px-3 py-2 text-sm font-semibold border ${
                        liveWinnerId === liveLeftId
                          ? "bg-emerald-700 text-white border-emerald-700"
                          : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                      } disabled:opacity-50`}
                    >
                      Winner: {liveLeftLabel}
                    </button>
                    <button
                      type="button"
                      onClick={() => setWinnerForSide("right")}
                      disabled={!liveRightId}
                      className={`rounded-xl px-3 py-2 text-sm font-semibold border ${
                        liveWinnerId === liveRightId
                          ? "bg-emerald-700 text-white border-emerald-700"
                          : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                      } disabled:opacity-50`}
                    >
                      Winner: {liveRightLabel}
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => bumpLive("left", -liveStep)}
                      className={`${buttonPadBase} ${buttonPadRose}`}
                    >
                      {liveLeftLabel} -{liveStep}
                    </button>
                    <button
                      type="button"
                      onClick={() => bumpLive("left", liveStep)}
                      className={`${buttonPadBase} ${buttonPadEmerald}`}
                    >
                      {liveLeftLabel} +{liveStep}
                    </button>
                    <button
                      type="button"
                      onClick={() => bumpLive("right", -liveStep)}
                      className={`${buttonPadBase} ${buttonPadRose}`}
                    >
                      {liveRightLabel} -{liveStep}
                    </button>
                    <button
                      type="button"
                      onClick={() => bumpLive("right", liveStep)}
                      className={`${buttonPadBase} ${buttonPadEmerald}`}
                    >
                      {liveRightLabel} +{liveStep}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={resetLiveScore}
                      className="rounded-xl px-3 py-2 text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
                    >
                      Reset Score
                    </button>
                    <button
                      type="button"
                      onClick={syncLiveToForm}
                      className="rounded-xl px-3 py-2 text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
                    >
                      Sync To Form
                    </button>
                    <button
                      type="button"
                      onClick={() => saveLive(false)}
                      className={`${buttonPadBase} ${buttonPadBlue}`}
                    >
                      Save Live Result
                    </button>
                    <button
                      type="button"
                      onClick={() => saveLive(true)}
                      className={`${buttonPadBase} ${buttonPadEmerald}`}
                    >
                      Save & Next Match
                    </button>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                    Quick flow: select match, score with large buttons, tap winner, then save.
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="bg-white rounded-xl shadow p-5">
            <div className="grid sm:grid-cols-5 gap-2 mb-3">
              <select
                value={selectedGameId}
                onChange={(e) => {
                  setSelectedGameId(e.target.value);
                  setSelectedClassificationId("");
                  setGeneratedMixedClassificationIds([]);
                  setGeneratedMixedLabel("");
                  setP1("");
                  setP2("");
                }}
                className="border p-2 rounded"
              >
                <option value="">All Games</option>
                {games.map((g) => (
                  <option key={g.id} value={g.id}>{g.game_name}</option>
                ))}
              </select>
              <select
                value={selectedClassificationId}
                onChange={(e) => setSelectedClassificationId(e.target.value)}
                className="border p-2 rounded"
              >
                <option value="">All Classifications</option>
                {generatedMixedClassificationIds.length >= 2 ? (
                  <option value="__mixed__">{generatedMixedLabel || "Mixed Classifications"}</option>
                ) : null}
                {classificationOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
              <input
                value={roundName}
                onChange={(e) => setRoundName(e.target.value)}
                className="border p-2 rounded"
                placeholder="Round name"
              />
              <select value={p1} onChange={(e) => setP1(e.target.value)} className="border p-2 rounded">
                <option value="">{isTeamMode ? "Select Team 1" : "Select Player 1"}</option>
                {p1Options.map((p) => (
                  <option key={`p1-${isTeamMode ? p.id : p.registration_id}`} value={isTeamMode ? p.id : p.registration_id}>
                    {isTeamMode ? p.team_name : `${p.name} - ${p.game_name}`}
                  </option>
                ))}
              </select>
              <select value={p2} onChange={(e) => setP2(e.target.value)} className="border p-2 rounded">
                <option value="">{isTeamMode ? "Select Team 2" : "Select Player 2"}</option>
                {p2Options.map((p) => (
                  <option key={`p2-${isTeamMode ? p.id : p.registration_id}`} value={isTeamMode ? p.id : p.registration_id}>
                    {isTeamMode ? p.team_name : `${p.name} - ${p.game_name}`}
                  </option>
                ))}
              </select>
              <button onClick={createMatch} className="bg-blue-600 text-white px-4 py-2 rounded">
                Create Match
              </button>
            </div>
            {appliedGeneratedSheet ? (
              <div className="mb-3 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-900">
                <span className="font-semibold">Applied from generated score sheet:</span>{" "}
                {appliedGeneratedSheet.mode} | {appliedGeneratedSheet.gameName} | {appliedGeneratedSheet.categoryText}
                {appliedGeneratedSheet.totalPlayers !== null ? ` | Total Players: ${appliedGeneratedSheet.totalPlayers}` : ""}
                {appliedGeneratedSheet.updatedAt && appliedGeneratedSheet.updatedAt !== "-"
                  ? ` | Updated: ${appliedGeneratedSheet.updatedAt}`
                  : ""}
              </div>
            ) : null}
            {isTeamMode && !filteredTeams.length ? (
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                No teams available for this doubles/team game. Create teams first in Team Manager.
                <button
                  type="button"
                  onClick={() => navigate(`/admin/results/team/${event_id}?${teamManagerQuery}`)}
                  className="ml-2 rounded bg-amber-700 px-2 py-1 text-white hover:bg-amber-800"
                >
                  Open Team Manager
                </button>
              </div>
            ) : null}

            <ul className="space-y-2">
              {matches.map((m) => {
                const matchIsTeam = isTeamBracketMatch(m);
                const side1Id = matchIsTeam ? m.team1_id : m.player1_reg_id;
                const side2Id = matchIsTeam ? m.team2_id : m.player2_reg_id;
                const side1Label = matchIsTeam
                  ? (m.team1_name || (hasSideId(side1Id) ? "Team 1" : "BYE"))
                  : (m.player1_name || (hasSideId(side1Id) ? "Player 1" : "BYE"));
                const side2Label = matchIsTeam
                  ? (m.team2_name || (hasSideId(side2Id) ? "Team 2" : "BYE"))
                  : (m.player2_name || (hasSideId(side2Id) ? "Player 2" : "BYE"));
                const winnerValue = winnerByMatch[m.id] ?? (m.winner_id ? String(m.winner_id) : "");

                return (
                  <li key={m.id} className="border p-3 rounded">
                    <div className="font-medium mb-2">
                      Round {m.round_number || 1} - {side1Label} vs {side2Label}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <input
                        placeholder="Score e.g. 21-15,21-17"
                        value={scoreByMatch[m.id] ?? m.score ?? ""}
                        onChange={(e) =>
                          setScoreByMatch((prev) => ({ ...prev, [m.id]: e.target.value }))
                        }
                        className="border p-2 rounded"
                      />
                      <select
                        value={winnerValue}
                        onChange={(e) =>
                          setWinnerByMatch((prev) => ({ ...prev, [m.id]: e.target.value }))
                        }
                        className="border p-2 rounded"
                      >
                        <option value="">Select winner</option>
                        {hasSideId(side1Id) ? (
                          <option value={side1Id}>
                            {side1Label}
                          </option>
                        ) : null}
                        {hasSideId(side2Id) ? (
                          <option value={side2Id}>
                            {side2Label}
                          </option>
                        ) : null}
                      </select>
                      <button
                        onClick={() => {
                          const score = scoreByMatch[m.id] ?? m.score ?? "";
                          const winner = winnerByMatch[m.id] ?? "";
                          saveResult(m, winner, score);
                        }}
                        className="bg-green-600 text-white px-3 py-2 rounded"
                      >
                        Save Result
                      </button>
                      <button
                        onClick={() => {
                          setLiveMatchId(String(m.id));
                          setGameMode("live");
                        }}
                        className="bg-indigo-600 text-white px-3 py-2 rounded"
                      >
                        Open Live Pad
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default BracketManager;
